import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  CalendarDays, CheckCircle2, XCircle, AlertTriangle, Pencil, Ban, History,
  Loader2, CreditCard, ArrowRight, Copy, ChevronDown, ChevronUp,
  ClipboardList, Plus, Trash2, StickyNote, ExternalLink, Mail,
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtEUR = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0);

// ─── SUB-COMPONENTS ───────────────────────────────

function TasksSection({ reservation, token, onTasksChanged }) {
  const [tasks, setTasks] = useState([]);
  const [newTitle, setNewTitle] = useState('');

  const fetchTasks = useCallback(async () => {
    if (!reservation?.id) return;
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/tasks/reservation/${reservation.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setTasks(data);
    } catch { /* ignore */ }
  }, [reservation?.id, token]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const toggleTask = async (taskId) => {
    await axios.put(`${BACKEND_URL}/api/tasks/${taskId}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
    fetchTasks();
    onTasksChanged?.();
  };

  const addTask = async () => {
    if (!newTitle.trim()) return;
    await axios.post(`${BACKEND_URL}/api/tasks/reservation/${reservation.id}`, { title: newTitle, is_manual: true }, { headers: { Authorization: `Bearer ${token}` } });
    setNewTitle('');
    fetchTasks();
    onTasksChanged?.();
  };

  const deleteTask = async (taskId) => {
    await axios.delete(`${BACKEND_URL}/api/tasks/${taskId}`, { headers: { Authorization: `Bearer ${token}` } });
    fetchTasks();
    onTasksChanged?.();
  };

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-[#2C3E30] flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Checklist</h4>
      {tasks.length === 0 && <p className="text-xs text-[#888]">Aucune tache.</p>}
      {tasks.map(t => (
        <div key={t.id} className="flex items-center gap-2 group">
          <Checkbox checked={t.completed} onCheckedChange={() => toggleTask(t.id)} />
          <span className={`text-sm flex-1 ${t.completed ? 'line-through text-[#999]' : 'text-[#333]'}`}>{t.title}</span>
          {t.is_manual && <button onClick={() => deleteTask(t.id)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>}
        </div>
      ))}
      <div className="flex gap-2 mt-2">
        <Input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Nouvelle tache..." className="text-sm h-8" onKeyDown={e => e.key === 'Enter' && addTask()} />
        <Button size="sm" variant="outline" onClick={addTask} className="h-8 px-2"><Plus className="w-3 h-3" /></Button>
      </div>
    </div>
  );
}

function NotesSection({ reservation, token }) {
  const [notes, setNotes] = useState(reservation?.internal_notes || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/reservations/${reservation.id}/notes`, { notes }, { headers: { Authorization: `Bearer ${token}` } });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { /* ignore */ }
    setSaving(false);
  };

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-[#2C3E30] flex items-center gap-2"><StickyNote className="w-4 h-4" /> Notes internes</h4>
      <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="text-sm" placeholder="Notes privees..." />
      <Button size="sm" variant="outline" onClick={save} disabled={saving} className="h-8">
        {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : saved ? <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-600" /> : null}
        {saved ? 'Enregistre' : 'Enregistrer'}
      </Button>
    </div>
  );
}

// ─── CANCEL MODAL ─────────────────────────────────

function CancelModal({ reservation, open, onOpenChange, token, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open || !reservation?.id) return;
    setPreview(null); setResult(null); setReason('');
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/admin/reservations/${reservation.id}/cancel-preview`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPreview(r.data))
      .catch(() => setPreview({ error: true }))
      .finally(() => setLoading(false));
  }, [open, reservation?.id, token]);

  const confirmCancel = async () => {
    setConfirming(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/reservations/${reservation.id}/cancel`, { reason }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(data);
      onDone?.();
    } catch (err) {
      setResult({ error: err.response?.data?.detail || 'Erreur' });
    }
    setConfirming(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700"><Ban className="w-5 h-5" /> Annuler la reservation</DialogTitle>
          <DialogDescription>{reservation?.name} - {reservation?.arrival_date} au {reservation?.departure_date}</DialogDescription>
        </DialogHeader>

        {loading && <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#C4A962]" /></div>}

        {preview && !preview.error && !result && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">{preview.policy}</p>
                  <p className="text-xs text-amber-700 mt-1">Delai avant arrivee : {preview.days_until_arrival} jours</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-[#f5f5f0] rounded-lg p-3 text-center">
                <p className="text-[#888] text-xs">Montant paye</p>
                <p className="font-semibold text-[#2C3E30]">{fmtEUR(preview.amount_paid)}</p>
              </div>
              <div className={`rounded-lg p-3 text-center ${preview.refund_amount > 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                <p className="text-xs text-[#888]">Remboursement</p>
                <p className={`font-semibold ${preview.refund_amount > 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtEUR(preview.refund_amount)}</p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-[#333]">Raison de l'annulation</label>
              <Textarea data-testid="cancel-reason" value={reason} onChange={e => setReason(e.target.value)} rows={2} className="mt-1 text-sm" placeholder="Optionnel..." />
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Retour</Button>
              <Button data-testid="confirm-cancel-btn" variant="destructive" onClick={confirmCancel} disabled={confirming}>
                {confirming ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Ban className="w-4 h-4 mr-2" />}
                Confirmer l'annulation
              </Button>
            </DialogFooter>
          </div>
        )}

        {result && !result.error && (
          <div className="space-y-3 py-4">
            <div className="flex items-center justify-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-6 h-6" />
              <p className="font-semibold">Reservation annulee</p>
            </div>
            <div className="text-center text-sm text-[#666]">
              <p>{result.policy}</p>
              {result.refund_amount > 0 && <p className="font-medium text-emerald-700 mt-1">Remboursement : {fmtEUR(result.refund_amount)}</p>}
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Fermer</Button>
            </DialogFooter>
          </div>
        )}

        {result?.error && (
          <div className="py-4 text-center">
            <p className="text-red-600 text-sm">{result.error}</p>
            <DialogFooter className="mt-4"><Button variant="outline" onClick={() => setResult(null)}>Reessayer</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── MODIFY MODAL ─────────────────────────────────

function ModifyModal({ reservation, open, onOpenChange, token, onDone }) {
  const [arrDate, setArrDate] = useState(null);
  const [depDate, setDepDate] = useState(null);
  const [guestsLodging, setGuestsLodging] = useState('');
  const [guestsReception, setGuestsReception] = useState('');
  const [optBois, setOptBois] = useState(false);
  const [optSono, setOptSono] = useState(false);
  const [optVaisselle, setOptVaisselle] = useState(false);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [arrOpen, setArrOpen] = useState(false);
  const [depOpen, setDepOpen] = useState(false);

  useEffect(() => {
    if (!open || !reservation) return;
    setArrDate(reservation.arrival_date ? new Date(reservation.arrival_date + 'T00:00:00') : null);
    setDepDate(reservation.departure_date ? new Date(reservation.departure_date + 'T00:00:00') : null);
    setGuestsLodging(String(reservation.guests_lodging || 1));
    setGuestsReception(reservation.guests_reception ? String(reservation.guests_reception) : '');
    const opts = reservation.options || {};
    setOptBois(!!opts.bois);
    setOptSono(!!opts.sono);
    setOptVaisselle(!!opts.vaisselle);
    setReason(''); setPreview(null); setResult(null); setPreviewError('');
  }, [open, reservation]);

  const fetchPreview = useCallback(async () => {
    if (!reservation?.id) return;
    setPreviewLoading(true); setPreviewError('');
    try {
      const params = new URLSearchParams();
      if (arrDate) params.append('arrival_date', format(arrDate, 'yyyy-MM-dd'));
      if (depDate) params.append('departure_date', format(depDate, 'yyyy-MM-dd'));
      if (guestsLodging) params.append('guests_lodging', guestsLodging);
      if (guestsReception) params.append('guests_reception', guestsReception);
      params.append('options_bois', optBois);
      params.append('options_sono', optSono);
      params.append('options_vaisselle', optVaisselle);
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/reservations/${reservation.id}/modify-preview?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setPreview(data);
    } catch (err) {
      setPreviewError(err.response?.data?.detail || 'Erreur de calcul');
      setPreview(null);
    }
    setPreviewLoading(false);
  }, [reservation?.id, arrDate, depDate, guestsLodging, guestsReception, optBois, optSono, optVaisselle, token]);

  useEffect(() => {
    if (open && reservation) {
      const timer = setTimeout(fetchPreview, 300);
      return () => clearTimeout(timer);
    }
  }, [open, reservation, fetchPreview]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        arrival_date: arrDate ? format(arrDate, 'yyyy-MM-dd') : undefined,
        departure_date: depDate ? format(depDate, 'yyyy-MM-dd') : undefined,
        guests_lodging: parseInt(guestsLodging) || undefined,
        guests_reception: guestsReception ? parseInt(guestsReception) : undefined,
        options: { bois: optBois, sono: optSono, vaisselle: optVaisselle },
        reason,
      };
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/reservations/${reservation.id}/modify`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setResult(data);
      onDone?.();
    } catch (err) {
      setResult({ error: err.response?.data?.detail || 'Erreur' });
    }
    setSubmitting(false);
  };

  const isGiteSalle = reservation?.accommodation_type === 'gite-salle';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#2C3E30]"><Pencil className="w-5 h-5" /> Modifier la reservation</DialogTitle>
          <DialogDescription>{reservation?.name} - {reservation?.accommodation_type}</DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-[#666]">Arrivee</label>
                <Popover open={arrOpen} onOpenChange={setArrOpen}>
                  <PopoverTrigger asChild>
                    <Button data-testid="modify-arrival-date" variant="outline" className="w-full justify-start text-sm h-9 mt-1">
                      <CalendarDays className="w-4 h-4 mr-2 text-[#C4A962]" />
                      {arrDate ? format(arrDate, 'dd MMM yyyy', { locale: fr }) : 'Choisir'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={arrDate} onSelect={d => { setArrDate(d); setArrOpen(false); }} locale={fr} defaultMonth={arrDate || undefined} />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="text-xs font-medium text-[#666]">Depart</label>
                <Popover open={depOpen} onOpenChange={setDepOpen}>
                  <PopoverTrigger asChild>
                    <Button data-testid="modify-departure-date" variant="outline" className="w-full justify-start text-sm h-9 mt-1">
                      <CalendarDays className="w-4 h-4 mr-2 text-[#C4A962]" />
                      {depDate ? format(depDate, 'dd MMM yyyy', { locale: fr }) : 'Choisir'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={depDate} onSelect={d => { setDepDate(d); setDepOpen(false); }} locale={fr} disabled={date => arrDate && date <= arrDate} defaultMonth={depDate || arrDate || undefined} />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Guests */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-[#666]">Personnes a loger</label>
                <Input data-testid="modify-guests-lodging" type="number" min="1" value={guestsLodging} onChange={e => setGuestsLodging(e.target.value)} className="h-9 mt-1 text-sm" />
              </div>
              {isGiteSalle && (
                <div>
                  <label className="text-xs font-medium text-[#666]">Invites reception</label>
                  <Input data-testid="modify-guests-reception" type="number" min="0" value={guestsReception} onChange={e => setGuestsReception(e.target.value)} className="h-9 mt-1 text-sm" />
                </div>
              )}
            </div>

            {/* Options */}
            <div>
              <label className="text-xs font-medium text-[#666] mb-2 block">Options</label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={optBois} onCheckedChange={setOptBois} /> Bois cheminee</label>
                {isGiteSalle && <label className="flex items-center gap-2 text-sm"><Checkbox checked={optSono} onCheckedChange={setOptSono} /> Sono / DJ</label>}
                {isGiteSalle && <label className="flex items-center gap-2 text-sm"><Checkbox checked={optVaisselle} onCheckedChange={setOptVaisselle} /> Vaisselle</label>}
              </div>
            </div>

            {/* Price preview */}
            {previewLoading && <div className="text-center py-3"><Loader2 className="w-5 h-5 animate-spin text-[#C4A962] mx-auto" /></div>}
            {previewError && <p className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">{previewError}</p>}
            {preview && (
              <div className="bg-[#f5f5f0] rounded-lg p-4 space-y-2">
                <p className="text-xs font-medium text-[#888] uppercase">Recalcul tarifaire</p>
                {preview.new_breakdown.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-[#555]">{item.label}</span>
                    <span className="font-medium">{fmtEUR(item.amount)}</span>
                  </div>
                ))}
                <div className="border-t border-[#ddd] pt-2 mt-2 flex justify-between text-sm font-semibold">
                  <span>Nouveau total</span>
                  <span>{fmtEUR(preview.new_total)}</span>
                </div>
                {preview.difference !== 0 && (
                  <div className={`flex justify-between text-sm font-semibold pt-1 ${preview.difference > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                    <span>{preview.difference > 0 ? 'Supplement a payer' : 'Remboursement'}</span>
                    <span>{preview.difference > 0 ? '+' : ''}{fmtEUR(preview.difference)}</span>
                  </div>
                )}
                {preview.action === 'supplement' && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1"><CreditCard className="w-3 h-3" /> Un lien de paiement Stripe sera genere pour le client</p>
                )}
                {preview.action === 'refund' && (
                  <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Le remboursement sera enregistre en comptabilite</p>
                )}
              </div>
            )}

            {/* Reason */}
            <div>
              <label className="text-xs font-medium text-[#666]">Raison de la modification</label>
              <Textarea data-testid="modify-reason" value={reason} onChange={e => setReason(e.target.value)} rows={2} className="mt-1 text-sm" placeholder="Optionnel..." />
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
              <Button data-testid="confirm-modify-btn" onClick={submit} disabled={submitting || previewLoading || !!previewError} className="bg-[#C4A962] text-white hover:bg-[#b5945a]">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Pencil className="w-4 h-4 mr-2" />}
                Confirmer la modification
              </Button>
            </DialogFooter>
          </div>
        ) : result.error ? (
          <div className="py-4 text-center">
            <p className="text-red-600 text-sm">{result.error}</p>
            <DialogFooter className="mt-4"><Button variant="outline" onClick={() => setResult(null)}>Reessayer</Button></DialogFooter>
          </div>
        ) : (
          <div className="space-y-3 py-4">
            <div className="flex items-center justify-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-6 h-6" />
              <p className="font-semibold">Reservation modifiee</p>
            </div>
            <div className="text-center text-sm text-[#666] space-y-1">
              <p>Ancien total : {fmtEUR(result.old_total)} &rarr; Nouveau total : {fmtEUR(result.new_total)}</p>
              {result.supplement_amount > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                  <p className="text-amber-800 font-medium text-sm">Supplement de {fmtEUR(result.supplement_amount)} a regler</p>
                  {result.supplement_checkout_url && (
                    <div className="flex items-center justify-center gap-2 mt-2">
                      <a href={result.supplement_checkout_url} target="_blank" rel="noopener noreferrer" className="text-[#C4A962] hover:underline text-xs flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Lien de paiement Stripe
                      </a>
                      <button onClick={() => navigator.clipboard.writeText(result.supplement_checkout_url)} className="text-[#888] hover:text-[#333]"><Copy className="w-3 h-3" /></button>
                    </div>
                  )}
                </div>
              )}
              {result.refund_amount > 0 && <p className="text-emerald-700 font-medium">Remboursement : {fmtEUR(result.refund_amount)}</p>}
            </div>
            <DialogFooter><Button onClick={() => onOpenChange(false)}>Fermer</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── HISTORY SECTION ──────────────────────────────

function HistorySection({ reservation, token }) {
  const [history, setHistory] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!reservation?.id) return;
    axios.get(`${BACKEND_URL}/api/admin/reservations/${reservation.id}/history`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setHistory(r.data))
      .catch(() => {});
  }, [reservation?.id, token]);

  if (history.length === 0) return null;

  return (
    <div className="space-y-2">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-sm font-semibold text-[#2C3E30] hover:text-[#C4A962] transition-colors">
        <History className="w-4 h-4" /> Historique ({history.length})
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div className="space-y-2 pl-6 border-l-2 border-[#e5e5e0]">
          {history.map(h => (
            <div key={h.id} className="text-xs space-y-0.5">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={h.action === 'cancel' ? 'border-red-300 text-red-700 text-[10px]' : 'border-blue-300 text-blue-700 text-[10px]'}>
                  {h.action === 'cancel' ? 'Annulation' : 'Modification'}
                </Badge>
                <span className="text-[#999]">{new Date(h.created_at).toLocaleDateString('fr-FR')} {new Date(h.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              {h.reason && <p className="text-[#666] italic">"{h.reason}"</p>}
              {h.refund_amount > 0 && <p className="text-emerald-700">Remboursement : {fmtEUR(h.refund_amount)}</p>}
              {h.supplement_amount > 0 && <p className="text-amber-700">Supplement : {fmtEUR(h.supplement_amount)}</p>}
              {Object.entries(h.changes || {}).filter(([k]) => k !== 'options').map(([key, val]) => (
                <p key={key} className="text-[#888]">{key}: {String(val.from)} &rarr; {String(val.to)}</p>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────

export default function ReservationDetail({ reservation, open, onOpenChange, token, onTasksChanged }) {
  const [cancelOpen, setCancelOpen] = useState(false);
  const [modifyOpen, setModifyOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [emailStatus, setEmailStatus] = useState('idle');

  const handleDone = () => setRefreshKey(k => k + 1);
  const isCancelled = reservation?.status === 'cancelled' || reservation?.status === 'cancelled_overdue';

  const resendConfirmation = async () => {
    setEmailStatus('sending');
    try {
      await axios.post(`${BACKEND_URL}/api/admin/reservations/${reservation.id}/send-confirmation`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setEmailStatus('sent');
    } catch {
      setEmailStatus('error');
    }
  };

  if (!reservation) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reservation-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-[#2C3E30]">{reservation.name}</span>
              <Badge variant="outline" className={
                reservation.status === 'confirmed' ? 'border-emerald-300 text-emerald-700' :
                reservation.status === 'pending' ? 'border-amber-300 text-amber-700' :
                'border-red-300 text-red-700'
              }>
                {reservation.status === 'confirmed' ? 'Confirmee' : reservation.status === 'pending' ? 'En attente' : 'Annulee'}
              </Badge>
            </DialogTitle>
            <DialogDescription>{reservation.email} | {reservation.phone}</DialogDescription>
          </DialogHeader>

          <div className="space-y-5" key={refreshKey}>
            {/* Reservation info */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-[#f5f5f0] rounded-lg p-3">
                <p className="text-[#888] text-xs">Formule</p>
                <p className="font-medium text-[#2C3E30]">{reservation.accommodation_type}</p>
              </div>
              <div className="bg-[#f5f5f0] rounded-lg p-3">
                <p className="text-[#888] text-xs">Dates</p>
                <p className="font-medium text-[#2C3E30]">{reservation.arrival_date} &rarr; {reservation.departure_date}</p>
              </div>
              <div className="bg-[#f5f5f0] rounded-lg p-3">
                <p className="text-[#888] text-xs">Montant total</p>
                <p className="font-semibold text-[#2C3E30]">{fmtEUR(reservation.amount)}</p>
              </div>
              <div className="bg-[#f5f5f0] rounded-lg p-3">
                <p className="text-[#888] text-xs">Acompte</p>
                <p className="font-medium text-[#2C3E30]">{fmtEUR(reservation.deposit_amount)} ({reservation.payment_status || 'N/A'})</p>
              </div>
              <div className="bg-[#f5f5f0] rounded-lg p-3">
                <p className="text-[#888] text-xs">Personnes</p>
                <p className="font-medium text-[#2C3E30]">{reservation.guests_lodging || reservation.guests} pers.</p>
              </div>
              {reservation.event_type && (
                <div className="bg-[#f5f5f0] rounded-lg p-3">
                  <p className="text-[#888] text-xs">Evenement</p>
                  <p className="font-medium text-[#2C3E30]">{reservation.event_type}</p>
                </div>
              )}
            </div>

            {/* Action buttons */}
            {!isCancelled && (
              <div className="flex gap-2" data-testid="reservation-actions">
                <Button data-testid="modify-reservation-btn" variant="outline" size="sm" onClick={() => setModifyOpen(true)} className="flex-1 border-[#C4A962] text-[#C4A962] hover:bg-[#C4A962]/10">
                  <Pencil className="w-4 h-4 mr-2" /> Modifier
                </Button>
                {reservation.email && (
                  <Button
                    data-testid="resend-confirmation-btn"
                    variant="outline" size="sm"
                    disabled={emailStatus === 'sending'}
                    onClick={resendConfirmation}
                    className="flex-1 border-[#8A9A5B] text-[#8A9A5B] hover:bg-[#8A9A5B]/10"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {emailStatus === 'sending' ? 'Envoi...' : 'Renvoyer email'}
                  </Button>
                )}
                <Button data-testid="cancel-reservation-btn" variant="outline" size="sm" onClick={() => setCancelOpen(true)} className="flex-1 border-red-400 text-red-600 hover:bg-red-50">
                  <Ban className="w-4 h-4 mr-2" /> Annuler
                </Button>
              </div>
            )}
            {emailStatus === 'sent' && (
              <p data-testid="resend-confirmation-success" className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                Email de confirmation envoye a {reservation.email}.
              </p>
            )}
            {emailStatus === 'error' && (
              <p data-testid="resend-confirmation-error" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">
                Echec de l'envoi de l'email. Reessayez ou verifiez la configuration Resend.
              </p>
            )}

            {isCancelled && reservation.cancellation_reason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-red-700 text-xs">Raison de l'annulation :</p>
                <p className="text-red-600">{reservation.cancellation_reason}</p>
              </div>
            )}

            {/* History */}
            <HistorySection reservation={reservation} token={token} />

            {/* Tasks */}
            <TasksSection reservation={reservation} token={token} onTasksChanged={onTasksChanged} />

            {/* Notes */}
            <NotesSection reservation={reservation} token={token} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Sub-modals */}
      <CancelModal reservation={reservation} open={cancelOpen} onOpenChange={setCancelOpen} token={token} onDone={handleDone} />
      <ModifyModal reservation={reservation} open={modifyOpen} onOpenChange={setModifyOpen} token={token} onDone={handleDone} />
    </>
  );
}
